import { Sparkles, Zap, Bug } from "lucide-react";

export default function V1_1_0() {
  return (
    <div className="tw-space-y-6">

      <div>
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-3">
          <Sparkles size={20} className="tw-text-yellow-500" />
          Nouvelles fonctionnalités
        </h3>
        <ul className="tw-list-disc tw-pl-8 tw-space-y-2 tw-text-gray-700">
          <li>
            <strong>Messages garant</strong> — Une nouvelle entrée « Messages garant »
            dans le menu de gauche, avec un compteur dédié. Un filtre rapide
            « En attente messages garant » est aussi disponible sur la liste des SAV.
            Les compteurs se mettent à jour automatiquement dès qu'un message est traité.
          </li>
          <li>
            <strong>Modifier qui doit agir</strong> — Un nouveau bouton « Statuts d'attente »
            dans le menu Actions permet de corriger manuellement qui est en attente
            (bénéficiaire, gestionnaire ou garant) quand un SAV est bloqué sur un
            statut qui ne correspond plus à la réalité.
          </li>
          <li>
            <strong>Historique enrichi</strong> — La timeline d'un SAV affiche désormais
            beaucoup plus d'événements (signatures, changements de gestionnaire, enquête
            de satisfaction, blocage juridique, modifications manuelles…), avec des
            icônes et couleurs pour les repérer en un coup d'œil.
          </li>
          <li>
            <strong>Carte et itinéraire vers le garant</strong> — Sur l'écran
            « Le garant est un réparateur », vous voyez maintenant la carte avec
            l'itinéraire routier réel entre le lieu de la panne et l'atelier,
            avec la distance et le temps de trajet (au lieu d'une distance à vol d'oiseau).
          </li>
          <li>
            <strong>Trois boutons de téléchargement permanents</strong> — Dans le panneau
            « Résumé de la signature » à droite, accès direct au document original,
            au document signé par le garant et au document final signé par les deux parties.
            Les boutons sont visibles à toutes les étapes, désactivés tant que le document
            n'est pas encore disponible.
          </li>
          <li>
            <strong>Bandeau d'annonce</strong> — Un bandeau peut désormais s'afficher
            en haut de l'application pour communiquer des annonces ou prévenir d'une
            maintenance. Vous pouvez le fermer et il ne réapparaît plus.
          </li>
          <li>
            <strong>Saisie téléphone internationale</strong> — Les champs téléphone
            acceptent désormais tous les indicatifs internationaux avec mise en forme
            automatique et drapeau du pays. Les noms et prénoms sont nettoyés
            automatiquement (espaces en trop) avant l'envoi pour signature.
          </li>
          <li>
            <strong>Re-synchroniser une signature (admin / superviseur)</strong> —
            Dans le panneau « Résumé de la signature », deux nouveaux boutons :
            un pour re-jouer la synchronisation si une notification s'est perdue,
            et un pour forcer le re-téléchargement du document final si le document
            stocké n'est pas le bon.
          </li>
          <li>
            <strong>Panneau de diagnostic signature (admin / superviseur)</strong> —
            Un nouvel onglet « Signature » dans le bouton Debug du SAV affiche l'état
            complet d'une signature en un coup d'œil. Utile quand un cas remonté ne
            semble pas cohérent et qu'on a besoin de vérifier ce qui s'est vraiment passé.
          </li>
        </ul>
      </div>

      <div>
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-3">
          <Zap size={20} className="tw-text-blue-500" />
          Améliorations
        </h3>
        <ul className="tw-list-disc tw-pl-8 tw-space-y-2 tw-text-gray-700">
          <li>
            <strong>Connexion plus stable</strong> — Vous n'êtes plus déconnecté
            automatiquement si le serveur est temporairement indisponible. Vous
            êtes déconnecté uniquement quand votre session a vraiment expiré.
          </li>
          <li>
            <strong>Page SAV plus rapide</strong> — Le chargement d'un SAV est
            nettement plus rapide grâce à plusieurs optimisations en coulisses.
          </li>
          <li>
            <strong>Application plus légère</strong> — L'application met moins de
            temps à se charger au premier accès.
          </li>
          <li>
            <strong>Badge UpSell</strong> — Les contrats issus d'une vente UpSell
            sont maintenant identifiés par un badge dans la liste des contrats.
          </li>
          <li>
            <strong>Statuts d'attente plus fiables</strong> — Suppression d'anciens
            statuts d'attente qui n'avaient plus de sens. Les indicateurs d'attente
            ne s'effacent plus mutuellement : par exemple, un nouveau message du
            bénéficiaire ne « met plus en pause » l'attente côté garant.
          </li>
          <li>
            <strong>Historique anciens SAV plus propre</strong> — Le SAV que vous
            consultez n'apparaît plus dans la liste de ses propres anciens SAV
            ni dans son compteur.
          </li>
        </ul>
      </div>

      <div>
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-3">
          <Bug size={20} className="tw-text-red-500" />
          Corrections
        </h3>
        <ul className="tw-list-disc tw-pl-8 tw-space-y-2 tw-text-gray-700">
          <li>
            <strong>Plafond financier compté en double</strong> — Sur les nouveaux SAV,
            le total « pris en charge » et le « plafond utilisé » étaient gonflés
            (jusqu'à 4 fois la vraie valeur). Les montants affichés sont maintenant exacts.
          </li>
          <li>
            <strong>Document signé final correct</strong> — Le document téléchargé après
            signature contient désormais bien les deux signatures visibles. Avant,
            dans certains cas le document récupéré ne montrait pas la signature
            du bénéficiaire.
          </li>
          <li>
            <strong>Téléchargement du bon document</strong> — Quand plusieurs versions
            d'une décision existaient sur un même SAV (anciennes versions annulées
            comprises), le bouton de téléchargement pouvait afficher une mauvaise
            version. Le bon document est maintenant systématiquement servi.
          </li>
          <li>
            <strong>« Voir le PDF » cohérent à chaque étape</strong> — Sur l'étape
            Signature Garant, le bouton « Voir le PDF » montre maintenant le document
            signé par le garant dès qu'il existe, plutôt qu'une version non signée.
          </li>
          <li>
            <strong>Le garant signait au mauvais endroit</strong> — Correction d'un cas
            où le garant cliquait sur son bouton « Signer » depuis son espace garant
            et se retrouvait à signer à la place du bénéficiaire. Le bon lien est
            désormais toujours utilisé.
          </li>
          <li>
            <strong>Décision marquée comme signée plus rapidement</strong> — Si une
            notification de signature tarde ou se perd, la décision est quand même
            marquée comme signée dès que les deux parties ont signé. Plus de cas
            où une décision restait bloquée en attente alors que tout est signé.
          </li>
          <li>
            <strong>Analyse IA sur les nouveaux contrats</strong> — L'analyse de
            couverture par l'IA fonctionne à nouveau sur les SAV liés à des contrats
            créés dans le nouvel outil Pro.
          </li>
          <li>
            <strong>Recherche d'atelier plus claire</strong> — Quand le service de
            recherche d'adresse est en panne, vous voyez un message d'indisponibilité
            technique au lieu d'une erreur qui laissait penser à un problème de saisie.
          </li>
          <li>
            <strong>Boutons accessibles sur petit écran</strong> — Sur la page
            « Le garant est un réparateur », la page est maintenant scrollable quand
            le contenu dépasse. Les boutons d'action ne sont plus masqués en bas
            sur les écrans plus petits.
          </li>
        </ul>
      </div>

    </div>
  );
}
